import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { uploadImage } from "@/lib/upload";
import { useUser } from "@/context/UserContext";
import { departmentList } from "@/mocks/messages";

interface Conversation {
  id: string;
  employee_profile_id: string;
  department_id: string;
  status: "waiting" | "processing" | "resolved" | "closed";
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_employee_count: number;
}

interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_profile_id: string;
  sender_type: "employee" | "department";
  message_type: "text" | "image" | "file" | "system";
  content: string;
  is_edited: boolean;
  is_deleted: boolean;
  reply_to_message_id: string | null;
  created_at: string;
  sender_name?: string;
}

interface Department {
  id: string;
  name: string;
  icon: string;
}

type MobileView = "departments" | "chat";

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  if (d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
    return d.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit", hour12: false });
  }
  return d.toLocaleDateString("zh-TW", { month: "2-digit", day: "2-digit" }) + " " +
    d.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit", hour12: false });
}

const STATUS_LABELS: Record<string, string> = {
  waiting: "等待處理",
  processing: "處理中",
  resolved: "已解決",
  closed: "已關閉",
};

const STATUS_COLORS: Record<string, string> = {
  waiting: "bg-accent-100 text-accent-700",
  processing: "bg-primary-100 text-primary-700",
  resolved: "bg-secondary-100 text-secondary-700",
  closed: "bg-background-200 text-foreground-500",
};

export default function DepartmentChatPage() {
  const { profileId, userName } = useUser();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDept, setSelectedDept] = useState<Department | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(true);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [expandedImages, setExpandedImages] = useState<Set<string>>(new Set());
  const [deptUnreadCounts, setDeptUnreadCounts] = useState<Record<string, number>>({});
  const [mobileView, setMobileView] = useState<MobileView>("departments");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageTimeRef = useRef<string | null>(null);
  const selectedDeptRef = useRef<Department | null>(null);
  const effectiveProfileIdRef = useRef<string | null>(null);

  // Keep ref in sync for use in callbacks
  useEffect(() => {
    selectedDeptRef.current = selectedDept;
  }, [selectedDept]);

  // Sync lastMessageTimeRef with latest message timestamp
  useEffect(() => {
    if (messages.length > 0) {
      lastMessageTimeRef.current = messages[messages.length - 1].created_at;
    } else {
      lastMessageTimeRef.current = null;
    }
  }, [messages]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleImageExpand = (msgId: string) => {
    setExpandedImages((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  // Upload image via multipart to local backend (replaces base64)
  // See @/lib/upload

  // Handle image upload
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const effectiveProfileId = effectiveProfileIdRef.current || profileId;
    if (!file || !conversation || !effectiveProfileId) return;

    if (!file.type.startsWith("image/")) {
      showToast("請選擇圖片檔案");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast("圖片大小不能超過 2MB");
      return;
    }

    try {
      // multipart upload → URL (replaces base64)
      const imageUrl = await uploadImage(file);

      const tempId = "temp-img-" + Date.now();
      const tempMsg: ChatMessage = {
        id: tempId,
        conversation_id: conversation.id,
        sender_profile_id: effectiveProfileId,
        sender_type: "employee",
        message_type: "image",
        content: imageUrl,
        is_edited: false,
        is_deleted: false,
        reply_to_message_id: null,
        created_at: new Date().toISOString(),
        sender_name: userName || "我",
      };
      setMessages((prev) => [...prev, tempMsg]);

      const { data: inserted, error } = await supabase.from("chat_messages").insert({
        conversation_id: conversation.id,
        sender_profile_id: effectiveProfileId,
        sender_type: "employee",
        message_type: "image",
        content: imageUrl,
      }).select("id").single();

      if (error) {
        showToast("圖片發送失敗：" + error.message);
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      } else if (inserted) {
        setMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, id: inserted.id } : m));
      }
    } catch {
      showToast("圖片處理失敗");
    }

    // Reset input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("departments")
        .select("id, name, icon")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setDepartments(data as Department[]);
      } else {
        setDepartments(departmentList.map((d) => ({
          id: d.id,
          name: d.label,
          icon: d.icon,
        })) as Department[]);
      }
    } catch {
      setDepartments(departmentList.map((d) => ({
        id: d.id,
        name: d.label,
        icon: d.icon,
      })) as Department[]);
    }
  }, []);

  // Fetch all department unread counts for this employee
  const fetchAllDeptUnreadCounts = useCallback(async () => {
    if (!profileId && !effectiveProfileIdRef.current) return;
    const pid = profileId || effectiveProfileIdRef.current;
    if (!pid) return;
    try {
      const { data } = await supabase
        .from("department_conversations")
        .select("department_id, unread_employee_count")
        .eq("employee_profile_id", pid);

      const counts: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        counts[row.department_id] = (counts[row.department_id] || 0) + (row.unread_employee_count || 0);
      });
      setDeptUnreadCounts(counts);
      // 即時廣播給 AppShell 導航徽章
      const total = Object.values(counts).reduce((s, n) => s + (n || 0), 0);
      window.dispatchEvent(new CustomEvent("deptchat:unread-sync", { detail: { total } }));
    } catch {
      // silent
    }
  }, [profileId]);

  // Direct query: get or create conversation
  const getOrCreateConversation = useCallback(async (departmentId: string) => {
    setConvLoading(true);
    setConvError(null);
    try {
      let activeProfileId = profileId;

      // If profileId is missing, try to fetch or auto-create from Supabase auth
      if (!activeProfileId) {
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id;
        if (!userId) {
          setConvError("尚未登入，無法建立對話。");
          setConvLoading(false);
          return null;
        }
        const { data: prof } = await supabase
          .from("profiles")
          .select("id")
          .eq("user_id", userId)
          .maybeSingle();
        if (prof) {
          activeProfileId = prof.id;
        } else {
          const fallbackName =
            (session.user.user_metadata?.name as string) ||
            (session.user.email?.split("@")[0] || "使用者");
          const { data: newProf, error: createError } = await supabase
            .from("profiles")
            .insert({
              user_id: userId,
              display_name: fallbackName,
              role: "employee",
              employee_id: null,
            })
            .select("id")
            .maybeSingle();
          if (createError || !newProf) {
            setConvError("使用者資料初始化失敗，請重新登入。");
            setConvLoading(false);
            return null;
          }
          activeProfileId = newProf.id;
        }
      }

      effectiveProfileIdRef.current = activeProfileId;

      // Try to find existing conversation
      const { data: existing, error: findError } = await supabase
        .from("department_conversations")
        .select("id, employee_profile_id, department_id, status, last_message_at, last_message_preview, unread_employee_count")
        .eq("employee_profile_id", activeProfileId)
        .eq("department_id", departmentId)
        .maybeSingle();

      if (findError) throw findError;

      if (existing) {
        setConversation(existing as Conversation);
        return existing as Conversation;
      }

      // Create new conversation
      const { data: created, error: createError } = await supabase
        .from("department_conversations")
        .insert({
          employee_profile_id: activeProfileId,
          department_id: departmentId,
          status: "waiting",
        })
        .select()
        .single();

      if (createError) throw createError;
      if (!created) throw new Error("建立對話失敗");

      setConversation(created as Conversation);
      return created as Conversation;
    } catch (err: any) {
      const msg = err?.message || err?.details || "載入對話失敗";
      setConvError(msg);
      showToast(msg);
      return null;
    } finally {
      setConvLoading(false);
    }
  }, [profileId]);

  // Direct query: fetch messages with enriched sender info
  const fetchMessages = useCallback(async (convId: string) => {
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select(`
          id, conversation_id, sender_profile_id, sender_type, message_type,
          content, is_edited, is_deleted, reply_to_message_id, created_at,
          sender:profiles!chat_messages_sender_profile_id_fkey(display_name)
        `)
        .eq("conversation_id", convId)
        .eq("is_deleted", false)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const enriched: ChatMessage[] = (data || []).map((row: any) => ({
        id: row.id,
        conversation_id: row.conversation_id,
        sender_profile_id: row.sender_profile_id,
        sender_type: row.sender_type,
        message_type: row.message_type,
        content: row.content,
        is_edited: row.is_edited,
        is_deleted: row.is_deleted,
        reply_to_message_id: row.reply_to_message_id,
        created_at: row.created_at,
        sender_name: row.sender?.display_name,
      }));

      setMessages(enriched);
    } catch (err: any) {
      showToast("讀取訊息失敗: " + (err?.message || ""));
      setMessages([]);
    }
  }, []);

  // Mark messages as read
  const markAsRead = useCallback(async (convId: string, deptId?: string) => {
    const effectiveProfileId = effectiveProfileIdRef.current || profileId;
    if (!effectiveProfileId) return;
    try {
      const { data: unreadMsgs } = await supabase
        .from("chat_messages")
        .select("id")
        .eq("conversation_id", convId)
        .neq("sender_profile_id", effectiveProfileId);

      if (unreadMsgs && unreadMsgs.length > 0) {
        const reads = unreadMsgs.map((m) => ({
          message_id: m.id,
          reader_profile_id: effectiveProfileId,
        }));

        await supabase.from("chat_message_reads").upsert(reads, {
          onConflict: "message_id, reader_profile_id",
          ignoreDuplicates: true,
        });
      }

      // Reset unread_employee_count in DB and local state
      await supabase
        .from("department_conversations")
        .update({ unread_employee_count: 0 })
        .eq("id", convId);

      if (deptId) {
        setDeptUnreadCounts((prev) => ({ ...prev, [deptId]: 0 }));
      }
    } catch {
      // Silently fail
    }
  }, [profileId]);

  // Fetch only new messages since last known timestamp (incremental polling)
  const fetchNewMessages = useCallback(async (convId: string) => {
    const since = lastMessageTimeRef.current;
    if (!since) return;

    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select(`
          id, conversation_id, sender_profile_id, sender_type, message_type,
          content, is_edited, is_deleted, reply_to_message_id, created_at,
          sender:profiles!chat_messages_sender_profile_id_fkey(display_name)
        `)
        .eq("conversation_id", convId)
        .eq("is_deleted", false)
        .gt("created_at", since)
        .order("created_at", { ascending: true });

      if (error || !data || data.length === 0) return;

      const enriched: ChatMessage[] = (data || []).map((row: any) => ({
        id: row.id,
        conversation_id: row.conversation_id,
        sender_profile_id: row.sender_profile_id,
        sender_type: row.sender_type,
        message_type: row.message_type,
        content: row.content,
        is_edited: row.is_edited,
        is_deleted: row.is_deleted,
        reply_to_message_id: row.reply_to_message_id,
        created_at: row.created_at,
        sender_name: row.sender?.display_name,
      }));

      setMessages((prev) => {
        const existingIds = new Set(prev.map((m) => m.id));
        const trulyNew = enriched.filter((m) => !existingIds.has(m.id));
        if (trulyNew.length === 0) return prev;
        return [...prev, ...trulyNew];
      });
    } catch {
      // Best-effort polling, silently ignore errors
    }
  }, []);

  // Real-time subscription
  useEffect(() => {
    if (!conversation?.id) return;

    const channel = supabase
      .channel(`dept-chat-${conversation.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_messages",
          filter: `conversation_id=eq.${conversation.id}`,
        },
        async (payload) => {
          const newMsg = payload.new as ChatMessage;
          const effectiveProfileId = effectiveProfileIdRef.current || profileId;
          // Skip own messages — optimistic update handles display
          if (newMsg.sender_profile_id === effectiveProfileId) return;
          // Mark as read if from other party
          if (effectiveProfileId) {
            supabase.from("chat_message_reads").upsert({
              message_id: newMsg.id,
              reader_profile_id: effectiveProfileId,
            }, {
              onConflict: "message_id, reader_profile_id",
              ignoreDuplicates: true,
            }).then(() => {});
          }
          // Incremental fetch: only get new messages
          fetchNewMessages(conversation.id);
          // If we're viewing this conversation, reset its unread count
          if (selectedDeptRef.current?.id === conversation.department_id) {
            supabase
              .from("department_conversations")
              .update({ unread_employee_count: 0 })
              .eq("id", conversation.id)
              .then(() => {});
            setDeptUnreadCounts((prev) => ({ ...prev, [conversation.department_id]: 0 }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation?.id, conversation?.department_id, profileId, fetchNewMessages]);

  // 5-second polling as reliable fallback
  useEffect(() => {
    if (!conversation?.id) return;

    const interval = setInterval(() => {
      fetchNewMessages(conversation.id);
    }, 5000);

    return () => clearInterval(interval);
  }, [conversation?.id, fetchNewMessages]);

  // Load departments on mount
  useEffect(() => {
    fetchDepartments().then(() => setLoading(false));
  }, [fetchDepartments]);

  // Load and poll department unread counts
  useEffect(() => {
    fetchAllDeptUnreadCounts();
    const interval = setInterval(fetchAllDeptUnreadCounts, 10000);
    return () => clearInterval(interval);
  }, [fetchAllDeptUnreadCounts]);

  // Broadcast dept-chat unread total to AppShell whenever counts change
  useEffect(() => {
    const total = Object.values(deptUnreadCounts).reduce((s, n) => s + (n || 0), 0);
    window.dispatchEvent(new CustomEvent("deptchat:unread-sync", { detail: { total } }));
  }, [deptUnreadCounts]);

  // When department is selected
  useEffect(() => {
    if (!selectedDept?.id) return;
    let cancelled = false;
    getOrCreateConversation(selectedDept.id).then((conv) => {
      if (!cancelled && conv) {
        fetchMessages(conv.id);
        markAsRead(conv.id, selectedDept.id);
      }
    });
    return () => { cancelled = true; };
  }, [selectedDept?.id, profileId, getOrCreateConversation, fetchMessages, markAsRead]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const effectiveProfileId = effectiveProfileIdRef.current || profileId;
    if (!inputText.trim() || !conversation || !effectiveProfileId) return;

    const content = inputText.trim();
    setInputText("");

    // Optimistic insert
    const tempId = "temp-" + Date.now();
    const tempMsg: ChatMessage = {
      id: tempId,
      conversation_id: conversation.id,
      sender_profile_id: effectiveProfileId,
      sender_type: "employee",
      message_type: "text",
      content,
      is_edited: false,
      is_deleted: false,
      reply_to_message_id: null,
      created_at: new Date().toISOString(),
      sender_name: userName || "我",
    };
    setMessages((prev) => [...prev, tempMsg]);

    try {
      const { data: inserted, error } = await supabase.from("chat_messages").insert({
        conversation_id: conversation.id,
        sender_profile_id: effectiveProfileId,
        sender_type: "employee",
        message_type: "text",
        content,
      }).select("id").single();

      if (error) {
        showToast("發送失敗：" + error.message);
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
      } else if (inserted) {
        setMessages((prev) => prev.map((m) => m.id === tempId ? { ...m, id: inserted.id } : m));
      }
    } catch (err: any) {
      showToast(err?.message || "發送失敗");
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    }
  };

  const handleDeptSelect = (dept: Department) => {
    // Immediately clear this department's unread count for instant feedback
    setDeptUnreadCounts((prev) => ({ ...prev, [dept.id]: 0 }));
    setSelectedDept(dept);
    setConversation(null);
    setMessages([]);
    setConvError(null);
    setExpandedImages(new Set());
    setMobileView("chat");
  };

  const handleRetry = () => {
    if (selectedDept) {
      getOrCreateConversation(selectedDept.id).then((conv) => {
        if (conv) {
          fetchMessages(conv.id);
          markAsRead(conv.id, selectedDept.id);
        }
      });
    }
  };

  return (
    <div className="h-full flex flex-col -m-4 md:-m-6 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2 rounded-lg bg-foreground-800 text-background-50 text-sm shadow-lg flex items-center gap-2">
          <i className="ri-error-warning-line" />
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="px-4 md:px-6 py-3 border-b border-background-200 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-lg font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
            部門通訊
          </h1>
          <p className="text-xs text-foreground-500">
            <span className="xl:hidden">
              {mobileView === "departments" ? "選擇部門開始對話" : `${selectedDept?.name || "部門"}・專屬聊天室`}
            </span>
            <span className="hidden xl:inline">選擇部門開始對話・自動建立專屬聊天室</span>
          </p>
        </div>
        {conversation && (
          <span className={`${mobileView === "chat" ? "inline-flex" : "hidden"} xl:inline-flex text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[conversation.status]}`}>
            {STATUS_LABELS[conversation.status]}
          </span>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left: Department List */}
        <div className={`${mobileView === "departments" ? "flex" : "hidden"} xl:flex w-full xl:w-56 shrink-0 border-r border-background-200 flex-col bg-background-50/50`}>
          <div className="px-3 py-3 border-b border-background-200">
            <span className="text-xs font-medium text-foreground-500 uppercase tracking-wider">
              <i className="ri-building-line mr-1" />
              部門列表
            </span>
          </div>
          <div className="flex-1 overflow-y-auto py-2 px-2 space-y-1">
            {loading ? (
              <div className="p-3 text-center">
                <i className="ri-loader-4-line animate-spin text-foreground-400" />
              </div>
            ) : (
              departments.map((dept) => {
                const unread = deptUnreadCounts[dept.id] || 0;
                return (
                <button
                  key={dept.id}
                  onClick={() => handleDeptSelect(dept)}
                  className={`w-full flex items-center gap-3 px-3 py-3.5 xl:gap-2 xl:py-2.5 rounded-lg text-sm transition-colors cursor-pointer ${
                    selectedDept?.id === dept.id
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${dept.icon} ${selectedDept?.id === dept.id ? "text-primary-500" : "text-foreground-400"}`} />
                  </div>
                  <span className="flex-1 text-left">{dept.name}</span>
                  {unread > 0 && (
                    <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-accent-500 text-background-50 text-[10px] flex items-center justify-center font-bold">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right: Chat Area */}
        <div className={`${mobileView === "chat" ? "flex" : "hidden"} xl:flex flex-1 flex-col min-w-0 bg-background-50`}>
          {selectedDept ? (
            <>
              {/* Chat Header */}
              <div className="px-3 xl:px-4 py-2.5 xl:py-3 border-b border-background-200 flex items-center gap-2 xl:gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setMobileView("departments")}
                  className="xl:hidden w-10 h-10 -ml-1 rounded-lg flex items-center justify-center shrink-0 text-foreground-600 hover:bg-background-100 active:bg-background-200"
                  aria-label="返回部門列表"
                >
                  <i className="ri-arrow-left-line text-lg" />
                </button>
                <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center shrink-0">
                  <i className={`${selectedDept.icon} text-primary-500 text-sm`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-semibold text-foreground-900 truncate">{selectedDept.name}</h2>
                  <p className="text-xs text-foreground-500 truncate">
                    {convLoading ? "載入中..."
                      : convError ? "建立失敗"
                      : conversation ? `對話已建立・${STATUS_LABELS[conversation.status]}`
                      : "建立對話中..."}
                  </p>
                </div>
                {convError && (
                  <button
                    onClick={handleRetry}
                    className="px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-refresh-line" />
                    重試
                  </button>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-3 xl:px-4 py-4 space-y-4 min-h-0 overscroll-contain">
                {convLoading ? (
                  <div className="flex flex-col items-center justify-center h-full">
                    <i className="ri-loader-4-line animate-spin text-primary-500 text-2xl" />
                    <p className="text-sm text-foreground-500 mt-2">載入對話...</p>
                  </div>
                ) : convError ? (
                  <div className="flex flex-col items-center justify-center h-full text-foreground-400">
                    <i className="ri-error-warning-line text-4xl mb-3 text-accent-500" />
                    <p className="text-sm font-medium text-foreground-700">對話建立失敗</p>
                    <p className="text-xs mt-1 text-foreground-500 max-w-xs text-center">{convError}</p>
                    <button
                      onClick={handleRetry}
                      className="mt-4 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                    >
                      <i className="ri-refresh-line" />
                      重新建立對話
                    </button>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-foreground-400">
                    <i className="ri-chat-smile-3-line text-4xl mb-3" />
                    <p className="text-sm font-medium">尚無訊息</p>
                    <p className="text-xs mt-1">發送第一則訊息給 {selectedDept.name}</p>
                  </div>
                ) : (
                  messages.map((msg) => {
                    const effectiveProfileId = effectiveProfileIdRef.current || profileId;
                    const isMe = msg.sender_type === "employee" && msg.sender_profile_id === effectiveProfileId;
                    const isSystem = msg.message_type === "system";
                    const displayName = msg.sender_type === "department"
                      ? (selectedDept?.name || "部門")
                      : (isMe ? "我" : (msg.sender_name || "未知"));

                    if (isSystem) {
                      return (
                        <div key={msg.id} className="flex justify-center">
                          <span className="text-xs text-foreground-400 bg-background-100 px-3 py-1 rounded-full">
                            {msg.content}
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div key={msg.id} className={`flex gap-2 xl:gap-3 ${isMe ? "flex-row-reverse" : ""}`}>
                        {!isMe && (
                          <div className="w-8 h-8 rounded-full bg-secondary-500 flex items-center justify-center text-background-50 text-xs font-semibold shrink-0">
                            {displayName.charAt(0)}
                          </div>
                        )}
                        <div className={`flex-1 min-w-0 ${isMe ? "text-right" : ""}`}>
                          <div className={`flex items-center gap-2 mb-1 ${isMe ? "justify-end" : ""}`}>
                            {!isMe && (
                              <span className="text-xs font-medium text-foreground-700">{displayName}</span>
                            )}
                            <span className="text-[10px] text-foreground-400">{formatTime(msg.created_at)}</span>
                            {msg.is_edited && (
                              <span className="text-[10px] text-foreground-400">(已編輯)</span>
                            )}
                          </div>
                          <div
                            className={`inline-block px-3 py-2 rounded-xl text-sm text-left ${
                              isMe
                                ? "bg-primary-500 text-background-50 rounded-br-none"
                                : "bg-background-100 text-foreground-800 rounded-bl-none"
                            }`}
                          >
                    {msg.message_type === "image" ? (
                      expandedImages.has(msg.id) ? (
                        <img
                          src={msg.content}
                          alt="圖片"
                          className="max-w-[70vw] xl:max-w-[300px] max-h-[300px] rounded-lg object-cover cursor-pointer"
                          loading="lazy"
                          onClick={() => toggleImageExpand(msg.id)}
                        />
                      ) : (
                        <span
                          className="italic cursor-pointer underline"
                          onClick={() => toggleImageExpand(msg.id)}
                        >
                          圖片
                        </span>
                      )
                    ) : msg.message_type === "file" ? (
                      <span className="text-background-50/80 italic">[檔案]</span>
                    ) : (
                      <p className="whitespace-pre-wrap break-words max-w-[78vw] xl:max-w-none">{msg.content}</p>
                    )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              {conversation?.status === "closed" ? (
                <div className="px-4 py-3 border-t border-background-200 text-center shrink-0">
                  <p className="text-sm text-foreground-500">此對話已關閉，無法再發送訊息</p>
                </div>
              ) : (
                <div className="px-2 xl:px-4 pt-2 xl:py-3 pb-[max(0.5rem,env(safe-area-inset-bottom))] border-t border-background-200 shrink-0 bg-background-50">
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="w-10 h-10 xl:w-8 xl:h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 cursor-pointer shrink-0"
                      aria-label="傳送圖片"
                    >
                      <i className="ri-image-line" />
                    </button>
                    <button className="hidden xl:flex w-8 h-8 items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-pointer">
                      <i className="ri-attachment-line" />
                    </button>
                    <input
                      type="text"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSend()}
                      placeholder={`傳訊息給 ${selectedDept.name}...`}
                      className="flex-1 min-w-0 h-10 px-3 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 bg-background-50"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!inputText.trim()}
                      className="w-10 h-10 xl:w-auto xl:h-auto xl:px-4 xl:py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-40 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1 shrink-0"
                      aria-label="送出訊息"
                    >
                      <i className="ri-send-plane-line" />
                      <span className="hidden xl:inline">送出</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-foreground-400">
              <i className="ri-message-3-line text-4xl mb-3" />
              <p className="text-sm font-medium">選擇一個部門開始對話</p>
              <p className="text-xs mt-1">點選左側部門即可開啟專屬聊天室</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
